// Piece rendering utilities - stroke overrides and colored piece variants

import React from 'react';
import { defaultPieces } from 'react-chessboard';
import { AUGMENT_COLOR_STYLES } from '../augments/AugmentDock';
import { AUGMENTS } from '../augments/catalog';

// Theme stroke constants
export const LIGHT_THEME_PIECE_STROKE = '#000000';
export const DARK_THEME_PIECE_STROKE = '#3b3b3b';
export const OVERRIDABLE_STROKE_VALUES = new Set(['#000000', '#000', 'black', 'rgb(0,0,0)', 'rgba(0,0,0,1)']);

/**
 * Normalize a stroke value for comparison
 */
export const normalizeStrokeValue = (value) => {
  if (!value) return null;
  return String(value).trim().toLowerCase().replace(/\s+/g, '');
};

/**
 * Check if a stroke value should be overridden
 */
export const shouldOverrideStroke = (value) => {
  const normalized = normalizeStrokeValue(value);
  return normalized ? OVERRIDABLE_STROKE_VALUES.has(normalized) : false;
};

/**
 * Recursively override stroke colors in a React element tree
 */
export const overridePieceStroke = (element, strokeColor) => {
  if (!React.isValidElement(element) || !strokeColor) return element;
  if (strokeColor === LIGHT_THEME_PIECE_STROKE) return element;
  let styleChanged = false;
  let propStrokeChanged = false;
  let childrenChanged = false;
  let nextStyle = element.props?.style;
  if (nextStyle && shouldOverrideStroke(nextStyle.stroke)) {
    nextStyle = { ...nextStyle, stroke: strokeColor };
    styleChanged = true;
  }
  if (shouldOverrideStroke(element.props?.stroke)) {
    propStrokeChanged = true;
  }
  let nextChildren = element.props?.children;
  if (nextChildren != null) {
    const mappedChildren = React.Children.map(nextChildren, (child) => {
      if (!React.isValidElement(child)) return child;
      const updatedChild = overridePieceStroke(child, strokeColor);
      if (updatedChild !== child) {
        childrenChanged = true;
      }
      return updatedChild;
    });
    if (childrenChanged) {
      nextChildren = mappedChildren;
    }
  }
  if (!styleChanged && !propStrokeChanged && !childrenChanged) {
    return element;
  }
  const nextProps = { ...element.props };
  if (styleChanged) nextProps.style = nextStyle;
  if (propStrokeChanged) nextProps.stroke = strokeColor;
  if (childrenChanged) nextProps.children = nextChildren;
  return React.cloneElement(element, nextProps);
};

/**
 * Get the stroke color for the current theme
 */
export const getPieceStrokeColor = (theme) => {
  return theme === 'dark' ? DARK_THEME_PIECE_STROKE : LIGHT_THEME_PIECE_STROKE;
};

/**
 * Build colored piece renderers with optional stroke override
 * Returns a map of pieceType -> renderer function
 */
export const buildColoredPiecesRenderer = (cache, applyStrokeOverride, strokeColor) => {
  const cacheKey = applyStrokeOverride ? `stroke:${strokeColor}` : 'base';
  if (cache[cacheKey]) return cache[cacheKey];
  
  const colors = {};
  Object.entries(AUGMENT_COLOR_STYLES).forEach(([name, def]) => {
    if (def?.hex) colors[name] = def.hex;
  });
  
  const wrapRenderer = (renderer, fillOverride = null) => {
    if (typeof renderer !== 'function') return () => null;
    return (props = {}) => {
      const forwardedProps = fillOverride != null ? { ...props, fill: fillOverride } : props;
      const element = renderer(forwardedProps);
      return applyStrokeOverride ? overridePieceStroke(element, strokeColor) : element;
    };
  };
  
  const result = {};
  Object.entries(defaultPieces).forEach(([key, renderer]) => {
    result[key] = wrapRenderer(renderer);
  });
  
  const whiteBases = ['P', 'N', 'B', 'R', 'Q', 'K'];
  whiteBases.forEach((t) => {
    const key = 'w' + t;
    Object.entries(colors).forEach(([name, hex]) => {
      const variantKey = key + '_' + name;
      result[variantKey] = wrapRenderer(defaultPieces[key], hex);
    });
  });
  
  cache[cacheKey] = result;
  return result;
};

/**
 * Get a UI piece renderer for a specific piece type
 */
export const getUiPieceRenderer = (cache, theme, pieceType) => {
  if (!pieceType) return null;
  const strokeColor = getPieceStrokeColor(theme);
  const map = buildColoredPiecesRenderer(cache, true, strokeColor);
  return map?.[pieceType] || null;
};

/**
 * Ensure a bench piece variant exists in the cache and return the unique key
 */
export const ensureBenchPieceVariant = (cache, theme, basePieceType, colorKey, benchId) => {
  if (!benchId) return basePieceType;
  const piecesMap = buildColoredPiecesRenderer(cache, false, null);
  const baseKey = basePieceType;
  let variantKey = baseKey;
  if (colorKey) {
    const suffix = String(colorKey).toLowerCase();
    const tintedKey = baseKey + '_' + suffix;
    if (piecesMap && typeof piecesMap[tintedKey] === 'function') {
      variantKey = tintedKey;
    }
  }
  const uniqueKey = variantKey + '__bench' + benchId;
  if (piecesMap && typeof piecesMap[uniqueKey] !== 'function') {
    const renderer =
      (variantKey !== baseKey && piecesMap && piecesMap[variantKey]) ||
      (piecesMap && piecesMap[baseKey]);
    if (typeof renderer === 'function') {
      piecesMap[uniqueKey] = (props) => renderer({ ...props });
      const strokeColor = getPieceStrokeColor(theme);
      const overrideMap = buildColoredPiecesRenderer(cache, true, strokeColor);
      if (overrideMap && typeof overrideMap[uniqueKey] !== 'function') {
        const overrideRenderer =
          (variantKey !== baseKey && overrideMap && overrideMap[variantKey]) ||
          (overrideMap && overrideMap[baseKey]);
        if (typeof overrideRenderer === 'function') {
          overrideMap[uniqueKey] = (props) => overrideRenderer({ ...props });
        }
      }
    }
  }
  return uniqueKey;
};

/**
 * Compute the UI position from a chess game state with augment color variants
 */
export const computeUiPosition = (
  game,
  prepUnitIdBySquare,
  unitIdBySquare,
  augmentsByUnitId,
  piecesMap
) => {
  try {
    const pos = {};
    if (!game) return pos;
    const board = game.board();
    const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
    for (let r = 0; r < 8; r++) {
      for (let c = 0; c < 8; c++) {
        const p = board[r][c];
        if (!p) continue;
        const square = files[c] + (8 - r);
        const base = (p.color === 'w' ? 'w' : 'b') + String(p.type || '').toUpperCase();
        let pieceType = base;
        if (p.color === 'w') {
          // Prefer current battle identity mapping (unitIdBySquare) over prep mapping.
          const battleMap = unitIdBySquare || {};
          const prepMap = prepUnitIdBySquare || {};
          const unitId = battleMap[square] || prepMap[square] || null;
          if (unitId) {
            const augMap = augmentsByUnitId || {};
            const augId = augMap?.[unitId]?.augmentId || null;
            const colorKey = augId ? (AUGMENTS[augId]?.color || null) : null;
            if (colorKey) {
              const suffix = String(colorKey).toLowerCase();
              const candidate = base + '_' + suffix;
              // Only apply variant if a renderer exists for it
              if (piecesMap && typeof piecesMap[candidate] === 'function') {
                pieceType = candidate;
              }
            }
          }
        }
        pos[square] = { pieceType };
      }
    }
    return pos;
  } catch (_) {
    return {};
  }
};






